import { useRef, useCallback, useEffect } from 'react';

/**
 * Double-Tap Handler Hook
 *
 * Detects double-tap/double-click by tracking timing between successive clicks.
 * Works with @use-gesture/react to provide consistent behavior across desktop and mobile.
 *
 * Note: Native onDoubleClick doesn't work reliably on touch devices, hence this approach.
 *
 * @param {Function} onDoubleTap - Callback executed on double-tap (receives gesture state)
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Max time (ms) between taps (default: 300)
 * @returns {Function} Click handler for useGesture's onClick
 *
 * @example
 * const handleDoubleTapLike = useDoubleTapHandler(() => handleLike());
 * const bind = useGesture({ onClick: handleDoubleTapLike });
 * <img {...bind()} />
 */
export const useDoubleTapHandler = (onDoubleTap, options = {}) => {
  const { threshold = 300 } = options;

  // Track the timestamp of the last tap
  const lastTapTimeRef = useRef(null); 

  // Track the timeout to reset tap timing after threshold expires
  const tapTimeoutRef = useRef(null);

  /**
   * Click handler that detects double-tap based on timing between successive clicks
   */
  const handleClick = useCallback((state) => {
    const currentTime = Date.now();
    const lastTapTime = lastTapTimeRef.current;

    // Check if this is a double-tap
    if (lastTapTime !== null && currentTime - lastTapTime < threshold) {

      // Double-tap detected! Execute callback with gesture state
      onDoubleTap(state);

      // Clear the timeout to prevent reset after successful double-tap
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
        tapTimeoutRef.current = null;
      }

      // Reset tap tracking to prevent triple-tap from triggering another double-tap
      lastTapTimeRef.current = null;
    } else {
      // First tap (or too much time elapsed) - record this tap as potential first tap
      lastTapTimeRef.current = currentTime;

      // Clear any existing timeout
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }

      // Set timeout to reset tap tracking after threshold
      // This prevents stale taps from triggering false double-tap detection
      tapTimeoutRef.current = setTimeout(() => {
        lastTapTimeRef.current = null;
      }, threshold);
    }
  }, [onDoubleTap, threshold]);

  /**
   * Cleanup timeout on component unmount to prevent memory leaks
   */
  useEffect(() => {
    return () => {
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
    };
  }, []);

  return handleClick;
};
